import { API_ENDPOINTS } from "@/constants/api";
import { http } from "@/lib/http-client";
import { unwrapData } from "@/lib/api-response";
import type {
  FaqCategory,
  FaqCategoryInput,
  FaqQuestion,
  FaqQuestionInput,
  ListFaqQuestionsQuery,
} from "@/types/faq";
import type { PaginatedResult } from "@/types/common";

interface FaqPagination {
  page: number;
  pageSize: number;
  total: number;
  pageCount: number;
}

const EMPTY_META = { total: 0, page: 1, limit: 25, totalPages: 1 };

/**
 * FAQ API.
 *
 * Unlike the OBS report service beside it, these routes ARE in the backend's ENVELOPE_PREFIXES,
 * so every response arrives as `{ success, message, data }` and goes through `unwrapData` like
 * every other governance screen. That was the choice for a reason: OBS opted out because a
 * rejected workbook upload has to say which column was wrong, and the envelope discards the body
 * on >= 400. FAQ writes are a code, two texts and a category — the envelope's uniform error
 * handling is worth more here than a bespoke error body.
 */
export const faqService = {
  async listCategories(): Promise<FaqCategory[]> {
    const response = await http.get(API_ENDPOINTS.FAQS.CATEGORIES);
    const data = unwrapData<FaqCategory[]>(response);
    return Array.isArray(data) ? data : [];
  },

  async createCategory(input: FaqCategoryInput): Promise<FaqCategory> {
    const response = await http.post(API_ENDPOINTS.FAQS.CATEGORIES, input);
    return unwrapData<FaqCategory>(response);
  },

  async updateCategory(
    key: string,
    input: Partial<Omit<FaqCategoryInput, "key">>,
  ): Promise<FaqCategory> {
    const response = await http.put(API_ENDPOINTS.FAQS.CATEGORY(key), input);
    return unwrapData<FaqCategory>(response);
  },

  async deleteCategory(key: string): Promise<void> {
    await http.delete(API_ENDPOINTS.FAQS.CATEGORY(key));
  },

  async listQuestions(
    query?: ListFaqQuestionsQuery,
  ): Promise<PaginatedResult<FaqQuestion>> {
    const response = await http.get(API_ENDPOINTS.FAQS.QUESTIONS, {
      params: query,
    });
    const data = unwrapData<{ items?: FaqQuestion[]; pagination?: FaqPagination }>(
      response,
    );
    const items = Array.isArray(data?.items) ? data.items : [];
    const pagination = data?.pagination;
    return {
      items,
      meta: pagination
        ? {
            total: pagination.total,
            page: pagination.page,
            limit: pagination.pageSize,
            totalPages: pagination.pageCount,
          }
        : EMPTY_META,
    };
  },

  async createQuestion(input: FaqQuestionInput): Promise<FaqQuestion> {
    const response = await http.post(API_ENDPOINTS.FAQS.QUESTIONS, input);
    return unwrapData<FaqQuestion>(response);
  },

  async updateQuestion(
    code: string,
    input: Partial<Omit<FaqQuestionInput, "code">>,
  ): Promise<FaqQuestion> {
    const response = await http.put(API_ENDPOINTS.FAQS.QUESTION(code), input);
    return unwrapData<FaqQuestion>(response);
  },

  async deleteQuestion(code: string): Promise<void> {
    await http.delete(API_ENDPOINTS.FAQS.QUESTION(code));
  },
};
